import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import toast from 'react-hot-toast';
import Header from './Header';
import AdminLOAs from './AdminLOAs';
import AdminCrossConnects from './AdminCrossConnects';
import './Admin.css';

const ADMIN_TABS = [
  { id: 'users', label: 'User management' },
  { id: 'loas', label: 'LOAs' },
  { id: 'cross-connects', label: 'Cross Connects' }
];

const Admin = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') || 'users';
  const setTab = (id) => setSearchParams(id === 'users' ? {} : { tab: id });
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);
  const [subscriptionsWithoutPortal, setSubscriptionsWithoutPortal] = useState([]);
  const [loadingStripe, setLoadingStripe] = useState(true);
  const [errorStripe, setErrorStripe] = useState(null);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.get('/api/auth/admin/users');
      setUsers(res.data.users || []);
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message || 'Failed to load users');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchStripeWithoutPortal = useCallback(async () => {
    try {
      setLoadingStripe(true);
      setErrorStripe(null);
      const res = await api.get('/api/auth/admin/stripe-without-portal');
      setSubscriptionsWithoutPortal(res.data.subscriptionsWithoutPortal || []);
    } catch (err) {
      setErrorStripe(err.response?.data?.error?.message || err.message || 'Failed to load Stripe data');
      setSubscriptionsWithoutPortal([]);
    } finally {
      setLoadingStripe(false);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !user?.isAdmin) {
      navigate('/dashboard', { replace: true });
      return;
    }
    fetchUsers();
    fetchStripeWithoutPortal();
  }, [isAuthenticated, user?.isAdmin, navigate, fetchUsers, fetchStripeWithoutPortal]);

  const handleSetAccess = async (targetUser, newActive) => {
    if (targetUser.id === user?.id) {
      toast.error('You cannot change your own access.');
      return;
    }
    setUpdatingId(targetUser.id);
    try {
      await api.patch(`/api/auth/admin/users/${targetUser.id}`, { isActive: newActive });
      toast.success(newActive ? 'Access restored.' : 'Access removed. User can no longer log in.');
      await fetchUsers();
    } catch (err) {
      const msg = err.response?.data?.error?.message || err.message || 'Failed to update access';
      toast.error(msg);
    } finally {
      setUpdatingId(null);
    }
  };

  const handleSetRole = async (targetUser, newRole) => {
    if (targetUser.id === user?.id) {
      toast.error('You cannot change your own role.');
      return;
    }
    setUpdatingId(targetUser.id);
    try {
      await api.patch(`/api/auth/admin/users/${targetUser.id}`, { role: newRole });
      toast.success(newRole === 'admin' ? 'User is now an admin.' : 'User is now a regular user.');
      await fetchUsers();
    } catch (err) {
      const msg = err.response?.data?.error?.message || err.message || 'Failed to update role';
      toast.error(msg);
    } finally {
      setUpdatingId(null);
    }
  };

  const formatDate = (d) => (d ? new Date(d).toLocaleString() : '—');

  const formatMonthlyPrice = (cents, interval) => {
    if (cents == null || cents === undefined) return '—';
    const amount = (cents / 100).toFixed(2);
    const per = (interval === 'year') ? '/year' : '/month';
    return `$${amount}${per}`;
  };

  const formatSubscription = (status) => {
    if (!status) return { label: 'No subscription', className: 'admin-sub-none' };
    const s = String(status).toLowerCase();
    if (s === 'active' || s === 'trialing') return { label: 'Active', className: 'admin-sub-active' };
    if (s === 'canceled' || s === 'cancelled') return { label: 'Canceled', className: 'admin-sub-canceled' };
    if (s === 'past_due' || s === 'unpaid') return { label: s === 'past_due' ? 'Past due' : 'Unpaid', className: 'admin-sub-pastdue' };
    return { label: status, className: 'admin-sub-other' };
  };

  if (!isAuthenticated) return null;
  if (!user?.isAdmin) return null;

  return (
    <div>
      <Header />
      <div className="admin-container">
        <nav className="admin-tabs" aria-label="Admin sections">
          {ADMIN_TABS.map(({ id, label }) => (
            <button
              key={id}
              type="button"
              className={`admin-tab ${tab === id ? 'admin-tab-active' : ''}`}
              onClick={() => setTab(id)}
            >
              {label}
            </button>
          ))}
        </nav>

        {tab === 'users' && (
        <>
        <div className="admin-card">
          <h1 className="admin-title">User management</h1>
          <p className="admin-subtitle">All registered users (admin only)</p>

          {loading && <p className="admin-loading">Loading users…</p>}
          {error && <p className="admin-error">{error}</p>}

          {!loading && !error && users.length === 0 && (
            <p className="admin-empty">No users found.</p>
          )}

          {!loading && !error && users.length > 0 && (
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Email</th>
                    <th>Username</th>
                    <th>Full name</th>
                    <th>Role</th>
                    <th>Change role</th>
                    <th>Status</th>
                    <th>Subscription</th>
                    <th>Price</th>
                    <th>Subscription Status</th>
                    <th>Created</th>
                    <th>Last login</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id}>
                      <td className="admin-cell-id">{u.id}</td>
                      <td>{u.email}</td>
                      <td>{u.username}</td>
                      <td>{u.fullName || '—'}</td>
                      <td className="admin-cell-role">
                        <span className={`admin-badge admin-badge-${(u.role === 'admin') ? 'admin' : 'user'}`}>
                          {u.role === 'admin' ? 'Admin' : 'User'}
                        </span>
                      </td>
                      <td className="admin-cell-change-role">
                        {u.id === user?.id ? (
                          <span className="admin-cell-you">—</span>
                        ) : u.role === 'admin' ? (
                          <button
                            type="button"
                            className="admin-btn admin-btn-role"
                            onClick={() => handleSetRole(u, 'user')}
                            disabled={updatingId === u.id}
                            title="Make regular user"
                          >
                            {updatingId === u.id ? '…' : 'Make user'}
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="admin-btn admin-btn-role admin-btn-make-admin"
                            onClick={() => handleSetRole(u, 'admin')}
                            disabled={updatingId === u.id}
                            title="Make admin"
                          >
                            {updatingId === u.id ? '…' : 'Make admin'}
                          </button>
                        )}
                      </td>
                      <td>
                        <span className={`admin-badge admin-badge-${u.isActive ? 'active' : 'inactive'}`}>
                          {u.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="admin-cell-product">
                        {u.subscriptionProductName || (u.subscriptionStatus ? 'Subscription' : '—')}
                      </td>
                      <td className="admin-cell-price">
                        {formatMonthlyPrice(u.subscriptionPriceCents, u.subscriptionInterval)}
                      </td>
                      <td>
                        {(() => {
                          const { label, className } = formatSubscription(u.subscriptionStatus);
                          return <span className={`admin-sub-badge ${className}`}>{label}</span>;
                        })()}
                      </td>
                      <td className="admin-cell-date">{formatDate(u.createdAt)}</td>
                      <td className="admin-cell-date">{formatDate(u.lastLogin)}</td>
                      <td>
                        {u.id === user?.id ? (
                          <span className="admin-cell-you">(you)</span>
                        ) : (
                          <button
                            type="button"
                            className={u.isActive ? 'admin-btn admin-btn-deactivate' : 'admin-btn admin-btn-activate'}
                            onClick={() => handleSetAccess(u, !u.isActive)}
                            disabled={updatingId === u.id}
                            title={u.isActive ? 'Remove access (user cannot log in)' : 'Restore access'}
                          >
                            {updatingId === u.id ? '…' : u.isActive ? 'Remove access' : 'Restore access'}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="admin-card admin-card-secondary">
          <h2 className="admin-title admin-title-secondary">Subscriptions without portal access</h2>
          <p className="admin-subtitle">
            Stripe customers who have a subscription but no portal account (e.g. purchased via a direct Stripe link).
          </p>

          {loadingStripe && <p className="admin-loading">Loading…</p>}
          {errorStripe && <p className="admin-error">{errorStripe}</p>}

          {!loadingStripe && !errorStripe && subscriptionsWithoutPortal.length === 0 && (
            <p className="admin-empty">None. Every Stripe subscription is linked to a portal user.</p>
          )}

          {!loadingStripe && !errorStripe && subscriptionsWithoutPortal.length > 0 && (
            <div className="admin-table-wrap">
              <table className="admin-table admin-table-secondary">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Product</th>
                    <th>Price</th>
                    <th>Status</th>
                    <th>Period end</th>
                    <th>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {subscriptionsWithoutPortal.map((s) => (
                    <tr key={s.subscriptionId}>
                      <td>{s.email}</td>
                      <td className="admin-cell-product">{s.productName}</td>
                      <td className="admin-cell-price">
                        {formatMonthlyPrice(s.priceCents, s.interval)}
                      </td>
                      <td>
                        {(() => {
                          const { label, className } = formatSubscription(s.status);
                          return <span className={`admin-sub-badge ${className}`}>{label}</span>;
                        })()}
                      </td>
                      <td className="admin-cell-date">
                        {s.currentPeriodEnd ? new Date(s.currentPeriodEnd).toLocaleDateString() : '—'}
                      </td>
                      <td className="admin-cell-date">
                        {s.created ? new Date(s.created).toLocaleDateString() : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="admin-card admin-card-secondary">
          <h2 className="admin-title admin-title-secondary">Users who canceled their subscription</h2>
          <p className="admin-subtitle">
            Portal users whose subscription status is canceled (includes cancel at period end).
          </p>

          {loading && <p className="admin-loading">Loading…</p>}
          {!loading && !error && (() => {
            const canceledUsers = users.filter((u) => {
              const s = String(u.subscriptionStatus || '').toLowerCase();
              return s === 'canceled' || s === 'cancelled' || u.cancelAtPeriodEnd === true;
            });
            if (canceledUsers.length === 0) {
              return <p className="admin-empty">None. No users with a canceled subscription.</p>;
            }
            return (
              <div className="admin-table-wrap">
                <table className="admin-table admin-table-secondary">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Email</th>
                      <th>Username</th>
                      <th>Full name</th>
                      <th>Subscription</th>
                      <th>Price</th>
                      <th>Status</th>
                      <th>Ends</th>
                      <th>Created</th>
                      <th>Last login</th>
                    </tr>
                  </thead>
                  <tbody>
                    {canceledUsers.map((u) => {
                      const isCanceling = u.cancelAtPeriodEnd && (u.subscriptionStatus === 'active' || u.subscriptionStatus === 'trialing');
                      const { label, className } = formatSubscription(
                        isCanceling ? 'canceling' : u.subscriptionStatus
                      );
                      const statusLabel = isCanceling ? 'Canceling (active until period end)' : label;
                      const statusClass = isCanceling ? 'admin-sub-pastdue' : className;
                      return (
                        <tr key={u.id}>
                          <td className="admin-cell-id">{u.id}</td>
                          <td>{u.email}</td>
                          <td>{u.username}</td>
                          <td>{u.fullName || '—'}</td>
                          <td className="admin-cell-product">
                            {u.subscriptionProductName || (u.subscriptionStatus ? 'Subscription' : '—')}
                          </td>
                          <td className="admin-cell-price">
                            {formatMonthlyPrice(u.subscriptionPriceCents, u.subscriptionInterval)}
                          </td>
                          <td>
                            <span className={`admin-sub-badge ${statusClass}`}>{statusLabel}</span>
                          </td>
                          <td className="admin-cell-date">
                            {u.cancelAt ? new Date(u.cancelAt).toLocaleDateString() : '—'}
                          </td>
                          <td className="admin-cell-date">{formatDate(u.createdAt)}</td>
                          <td className="admin-cell-date">{formatDate(u.lastLogin)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })()}
        </div>
        </>
        )}

        {tab === 'loas' && <AdminLOAs />}
        {tab === 'cross-connects' && <AdminCrossConnects />}
      </div>
    </div>
  );
};

export default Admin;
